function Message({ message, isOwnMessage, previousMessage }) {
  // Check if content exists in the message (handle both text and content fields)
  const messageContent = message.content || message.text || '';
  
  // Determine if this is from the same user as the previous message for grouping
  const isSameUser = previousMessage && previousMessage.username === message.username;
  
  // Style classes based on message owner and type
  const messageClass = isOwnMessage
    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md' 
    : 'bg-gray-700 text-gray-100 shadow-md';

  // For system messages (announcements, joins, etc.)
  if (message.type === 'system') {
    // Check if this is a join/leave message
    const isJoinMessage = messageContent.includes('has joined');
    const isLeaveMessage = messageContent.includes('has left') || messageContent.includes('disconnected');
    
    const iconClass = isJoinMessage 
      ? 'bg-green-900/40 text-green-400' 
      : isLeaveMessage 
        ? 'bg-red-900/40 text-red-400'
        : 'bg-blue-900/40 text-blue-400';
        
    const textClass = isJoinMessage 
      ? 'text-green-300' 
      : isLeaveMessage 
        ? 'text-red-300' 
        : 'text-gray-400';
    
    return (
      <div className="flex items-center justify-center gap-2 py-2 animate-fade-in">
        <div className="h-[1px] w-16 bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
        <div className={`rounded-full ${iconClass} p-1 flex-shrink-0`}>
          {isJoinMessage ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
          ) : isLeaveMessage ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        <span className={`text-sm bg-gray-800/80 ${textClass} px-4 py-1 rounded-full font-medium`}>
          {messageContent}
        </span>
        <div className="h-[1px] w-16 bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
      </div>
    );
  }

  return (
    <div className={`flex items-end space-x-2 group ${
      isOwnMessage ? 'justify-end' : 'justify-start'
    } ${!isSameUser ? 'mt-4' : 'mt-1'} animate-fade-in`}>
      
      {/* Avatar for other users */}
      {!isOwnMessage && !isSameUser && (
        <div className="w-8 h-8 flex-shrink-0 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center text-sm font-medium text-gray-300">
          {message.username.charAt(0).toUpperCase()}
        </div>
      )}
      
      {/* Spacer for grouped messages from same user */}
      {!isOwnMessage && isSameUser && (
        <div className="w-8 h-8 flex-shrink-0"></div>
      )}

      <div className={`flex flex-col space-y-1 max-w-[85%] sm:max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
        {/* Username - only shown for first message in a sequence */}
        {!isOwnMessage && !isSameUser && (
          <span className="text-xs text-gray-400 ml-1 mb-1">
            {message.username}
          </span>
        )}
        
        {/* Message bubble */}
        <div className={`rounded-2xl px-3 sm:px-4 py-2 break-words ${messageClass} 
          ${isOwnMessage ? 'rounded-br-sm' : 'rounded-bl-sm'}
          ${message.pending ? 'opacity-70' : 'opacity-100'}
          transition-all duration-200`}>
          <div className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
            {messageContent}
          </div>
          
          {/* Timestamp */}
          <div className={`text-[10px] mt-1 opacity-70 sm:opacity-40 sm:group-hover:opacity-100 transition-opacity
            ${isOwnMessage ? 'text-blue-100' : 'text-gray-400'}`}>
            {new Date(message.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      </div>

      {/* Avatar for own messages - only shown for first message in a sequence */}
      {isOwnMessage && !isSameUser && (
        <div className="w-8 h-8 flex-shrink-0 rounded-full bg-gradient-to-br from-blue-800 to-blue-900 flex items-center justify-center text-sm font-medium text-blue-300">
          {message.username.charAt(0).toUpperCase()}
        </div>
      )}
      
      {/* Spacer for grouped messages from same user */}
      {isOwnMessage && isSameUser && (
        <div className="w-8 h-8 flex-shrink-0"></div>
      )}
    </div>
  );
}

export default Message;